/**
 * Shared formatting utility for dashboard metric values.
 * Used by DashboardTable and future summary card components.
 */
import type { PaidMediaColumnDef } from 'src/store/leela-api';

export const defaultFormatCell = (value: unknown, format: PaidMediaColumnDef['format']): string => {
  if (value == null || (value === '' && (format === 'decimal' || format === 'integer'))) return '--';
  if (format === 'decimal') return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Format with thousands separator (e.g., 12345 → "12,345")
  if (format === 'integer') return Number(value).toLocaleString('en-US');
  return String(value);
};

/** Formats a decimal ratio as percentage (0.05 → "5.00%"), returns '--' for null/NaN. */
export const formatPercent = (value: unknown): string =>
  value != null && !isNaN(Number(value)) ? `${(Number(value) * 100).toFixed(2)}%` : '--';

/** Encode a string array as a single comma-separated URL param value. Empty array → ''. */
export const toArrayParam = (arr: string[]): string => {
  if (arr.length === 0) return '';
  // Escape % first (so our %2C escape isn't ambiguous), then comma (our delimiter)
  // Let URLSearchParams handle all other URL encoding (spaces, &, etc.)
  return arr.map(v => v.replace(/%/g, '%25').replace(/,/g, '%2C')).join(',');
};

/** Decode a comma-separated URL param value to a string array. Empty string → []. */
export const fromArrayParam = (value: string): string[] => {
  if (!value) return [];
  // Unescape comma first, then %, to reverse the encoding order
  return value.split(',').map(v => v.replace(/%2C/g, ',').replace(/%25/g, '%'));
};
